// Pure helpers for the UNIFIED private-notes section (one section per contact,
// not per subscription/group). Notes are stored PER-SUBSCRIPTION on the backend
// but presented as ONE flat, newest-first list under a single "Private notes 🔒"
// header with ONE "+ Add note" control. Extracted here so the merge/flatten and
// primary-subscription selection are unit-testable without a DOM.
import type { Contact, Note } from '../api/types.js';

// materializeNotes back-fills a subscription's notes array from a legacy single
// `note` string (one-element list) so callers can treat notes uniformly.
export function materializeNotes(c: Contact): Note[] {
  if (!c.notes) c.notes = c.note ? [{ body: c.note }] : [];
  return c.notes;
}

// flattenContactNotes concatenates notes across ALL of a contact's subscriptions
// into {contact, note} pairs, newest-first when timestamps allow (created_at,
// else updated_at; undated rows sink to the end). Each pair keeps its OWN
// subscription so edit/delete hit the right subscription_id + note id.
export function flattenContactNotes(subscriptions: Contact[]): { c: Contact; n: Note }[] {
  const rows: { c: Contact; n: Note }[] = [];
  for (const c of subscriptions) for (const n of materializeNotes(c)) rows.push({ c, n });
  const ts = (n: Note): number => {
    const t = n.created_at || n.updated_at;
    const v = t ? Date.parse(t) : NaN;
    return Number.isNaN(v) ? -Infinity : v;
  };
  return rows.sort((a, b) => ts(b.n) - ts(a.n));
}

// primaryNoteSubscription picks the STABLE, deterministic subscription that NEW
// notes attach to: the first non-revoked subscription if any, else the first
// subscription. (Comment: deterministic so "+ Add note" always targets a
// sensible, stable row regardless of how many groups the person is in.)
export function primaryNoteSubscription(subscriptions: Contact[]): Contact | undefined {
  return subscriptions.find((c) => c.status !== 'revoked' && c.sync_state !== 'revoked')
    || subscriptions[0];
}
